import type {
  SpreadsheetValidationErrorStyle,
  SpreadsheetValidationRule,
  SpreadsheetValidationRuleKind,
  SpreadsheetValidationRuleOperator,
} from '../protocol/spreadsheetEngineProtocol'

export type ExcelValidationRuleDraft = Omit<SpreadsheetValidationRule, 'id'> & { id?: string | null }
export type ValidationListSourceMode = 'values' | 'formula'

export type ValidationRuleAuthoringDefaults = {
  id: string
  kind: SpreadsheetValidationRuleKind
  operator: SpreadsheetValidationRuleOperator
  formula1: string
  formula2: string
  listValuesText: string
  listSourceMode: ValidationListSourceMode
  allowBlank: boolean
  inputTitle: string
  inputMessage: string
  errorStyle: SpreadsheetValidationErrorStyle
  errorTitle: string
  errorMessage: string
}

export type ValidationRuleAuthoringState = ValidationRuleAuthoringDefaults

export type ValidationRuleDraftInput = {
  currentRuleId?: string | null
  typedRuleId?: string | null
  kind: SpreadsheetValidationRuleKind
  operator: SpreadsheetValidationRuleOperator
  formula1: string
  formula2: string
  listValuesText: string
  listSourceMode: ValidationListSourceMode
  allowBlank: boolean
  inputTitle: string
  inputMessage: string
  errorStyle: SpreadsheetValidationErrorStyle
  errorTitle: string
  errorMessage: string
}

export type ValidationRuleAuthoringOption<T extends string = string> = {
  value: T
  label: string
  title?: string
}

export type ValidationRuleAuthoringTextField = {
  label: string
  title: string
  placeholder?: string
  inputMode?: 'decimal' | 'text' | 'url'
}

export type ValidationRuleAuthoringCheckField = {
  label: string
  title: string
}

export type ValidationRuleAuthoringSelectField<T extends string = string> = {
  label: string
  title: string
  options: ValidationRuleAuthoringOption<T>[]
}

export type ValidationRuleAuthoringPresentation = {
  currentRuleLabel: string | null
  kindField: ValidationRuleAuthoringSelectField<SpreadsheetValidationRuleKind>
  listSourceModeField: ValidationRuleAuthoringSelectField<ValidationListSourceMode>
  sourceField: ValidationRuleAuthoringTextField
  operatorField: ValidationRuleAuthoringSelectField<SpreadsheetValidationRuleOperator>
  firstFormulaField: ValidationRuleAuthoringTextField
  secondFormulaField: ValidationRuleAuthoringTextField
  allowBlankField: ValidationRuleAuthoringCheckField
  inputTitleField: ValidationRuleAuthoringTextField
  inputMessageField: ValidationRuleAuthoringTextField
  errorStyleField: ValidationRuleAuthoringSelectField<SpreadsheetValidationErrorStyle>
  errorTitleField: ValidationRuleAuthoringTextField
  errorMessageField: ValidationRuleAuthoringTextField
  showsListSource: boolean
  showsCustomFormula: boolean
  showsComparison: boolean
  showsSecondFormula: boolean
}

export type ValidationRuleAuthoringIssueField =
  | 'id'
  | 'source'
  | 'formula1'
  | 'formula2'

export type ValidationRuleAuthoringIssue = {
  field: ValidationRuleAuthoringIssueField
  message: string
}

export type ValidationRuleAuthoringPreview = {
  title: string
  lines: string[]
}

export type ValidationRuleAuthoringActionState = {
  canApply: boolean
  applyTitle: string
  issueTitle: string | null
}

export type ValidationRuleAuthoringModel = {
  presentation: ValidationRuleAuthoringPresentation
  draftInput: ValidationRuleDraftInput
  issues: ValidationRuleAuthoringIssue[]
  preview: ValidationRuleAuthoringPreview
  actionState: ValidationRuleAuthoringActionState
}

export type ValidationRuleAuthoringApplyPlan =
  | {
    kind: 'applyRule'
    rule: ExcelValidationRuleDraft
  }
  | {
    kind: 'blocked'
    issues: ValidationRuleAuthoringIssue[]
  }

export const VALIDATION_KIND_OPTIONS: ValidationRuleAuthoringOption<SpreadsheetValidationRuleKind>[] = [
  { value: 'list', label: 'List' },
  { value: 'wholeNumber', label: 'Whole number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
  { value: 'textLength', label: 'Text length' },
  { value: 'custom', label: 'Custom formula' },
]

export const VALIDATION_OPERATOR_OPTIONS: ValidationRuleAuthoringOption<SpreadsheetValidationRuleOperator>[] = [
  { value: 'between', label: 'between' },
  { value: 'notBetween', label: 'not between' },
  { value: 'equal', label: 'equal to' },
  { value: 'notEqual', label: 'not equal to' },
  { value: 'greaterThan', label: 'greater than' },
  { value: 'lessThan', label: 'less than' },
  { value: 'greaterThanOrEqual', label: 'greater than or equal to' },
  { value: 'lessThanOrEqual', label: 'less than or equal to' },
]

export const VALIDATION_ERROR_STYLE_OPTIONS: ValidationRuleAuthoringOption<SpreadsheetValidationErrorStyle>[] = [
  { value: 'stop', label: 'Stop' },
  { value: 'warning', label: 'Warning' },
  { value: 'information', label: 'Information' },
]

export const VALIDATION_LIST_SOURCE_MODE_OPTIONS: ValidationRuleAuthoringOption<ValidationListSourceMode>[] = [
  {
    value: 'values',
    label: 'Typed list',
    title: 'Use typed comma-separated list values',
  },
  {
    value: 'formula',
    label: 'Range or formula',
    title: 'Use an engine-evaluated range or formula source',
  },
]

function optionLabel<T extends string>(options: ValidationRuleAuthoringOption<T>[], value: T): string {
  return options.find((option) => option.value === value)?.label ?? value
}

function formulaInputModeForKind(kind: SpreadsheetValidationRuleKind): 'decimal' | 'text' {
  return kind === 'date' || kind === 'time' ? 'text' : 'decimal'
}

function operatorUsesTwoFormulas(operator: SpreadsheetValidationRuleOperator): boolean {
  return operator === 'between' || operator === 'notBetween'
}

function trimOrNull(value?: string | null): string | null {
  const trimmed = String(value ?? '').trim()
  return trimmed || null
}

function typedListValues(value: string): string[] {
  return value.split(',').map((entry) => entry.trim()).filter(Boolean)
}

function listValuesTextFromRule(rule?: SpreadsheetValidationRule | null): string {
  if (rule?.kind !== 'list') return 'Yes, No'
  return rule.formula1 ?? (rule.values ?? ['Yes', 'No']).join(', ')
}

export function buildValidationRuleAuthoringDefaults(args: {
  currentValidationId?: string | null
  currentValidationRule?: SpreadsheetValidationRule | null
}): ValidationRuleAuthoringDefaults {
  const rule = args.currentValidationRule
  return {
    id: rule?.id ?? args.currentValidationId ?? '',
    kind: rule?.kind ?? 'list',
    operator: rule?.operator ?? 'between',
    formula1: rule?.formula1 ?? '0',
    formula2: rule?.formula2 ?? '100',
    listValuesText: listValuesTextFromRule(rule),
    listSourceMode: rule?.kind === 'list' && rule?.formula1 ? 'formula' : 'values',
    allowBlank: rule?.allow_blank ?? true,
    inputTitle: rule?.input_title ?? '',
    inputMessage: rule?.input_message ?? '',
    errorStyle: rule?.error_style ?? 'stop',
    errorTitle: rule?.error_title ?? '',
    errorMessage: rule?.error_message ?? '',
  }
}

export function buildValidationRuleDraftInput(args: {
  currentRuleId?: string | null
  state: ValidationRuleAuthoringState
}): ValidationRuleDraftInput {
  return {
    currentRuleId: args.currentRuleId ?? null,
    typedRuleId: args.state.id,
    kind: args.state.kind,
    operator: args.state.operator,
    formula1: args.state.formula1,
    formula2: args.state.formula2,
    listValuesText: args.state.listValuesText,
    listSourceMode: args.state.listSourceMode,
    allowBlank: args.state.allowBlank,
    inputTitle: args.state.inputTitle,
    inputMessage: args.state.inputMessage,
    errorStyle: args.state.errorStyle,
    errorTitle: args.state.errorTitle,
    errorMessage: args.state.errorMessage,
  }
}

export function buildValidationRuleDraft(input: ValidationRuleDraftInput): ExcelValidationRuleDraft {
  const id = input.currentRuleId ?? trimOrNull(input.typedRuleId)
  const alertFields = {
    input_title: trimOrNull(input.inputTitle),
    input_message: trimOrNull(input.inputMessage),
    error_style: input.errorStyle,
    error_title: trimOrNull(input.errorTitle),
    error_message: trimOrNull(input.errorMessage),
  }

  if (input.kind === 'list') {
    const source = input.listValuesText.trim()
    return {
      id,
      kind: 'list',
      allow_blank: input.allowBlank,
      values: input.listSourceMode === 'formula'
        ? null
        : typedListValues(source),
      formula1: input.listSourceMode === 'formula' ? source : null,
      ...alertFields,
    }
  }

  if (input.kind === 'custom') {
    return {
      id,
      kind: 'custom',
      allow_blank: input.allowBlank,
      formula1: input.formula1.trim(),
      ...alertFields,
    }
  }

  return {
    id,
    kind: input.kind,
    operator: input.operator,
    allow_blank: input.allowBlank,
    formula1: input.formula1.trim(),
    formula2: (input.operator === 'between' || input.operator === 'notBetween')
      ? input.formula2.trim()
      : null,
    ...alertFields,
  }
}

export function validateValidationRuleDraft(input: ValidationRuleDraftInput): ValidationRuleAuthoringIssue[] {
  const issues: ValidationRuleAuthoringIssue[] = []
  const typedRuleId = String(input.typedRuleId ?? '').trim()
  if (typedRuleId && !/^[A-Za-z_][A-Za-z0-9_. -]*$/.test(typedRuleId)) {
    issues.push({
      field: 'id',
      message: 'Rule id must start with a letter or underscore and use letters, numbers, spaces, dots, underscores, or hyphens.',
    })
  }

  if (input.kind === 'list') {
    const source = input.listValuesText.trim()
    if (!source) {
      issues.push({
        field: 'source',
        message: 'List validation needs at least one allowed value or an engine-evaluated source range.',
      })
    } else if (input.listSourceMode === 'values' && typedListValues(source).length === 0) {
      issues.push({
        field: 'source',
        message: 'Typed list validation needs at least one non-empty value.',
      })
    }
    return issues
  }

  const formula1 = input.formula1.trim()
  if (!formula1) {
    issues.push({
      field: 'formula1',
      message: input.kind === 'custom'
        ? 'Custom validation needs an engine-evaluated formula.'
        : 'Validation comparison needs a value or formula.',
    })
  } else if (input.kind === 'custom' && !formula1.startsWith('=')) {
    issues.push({
      field: 'formula1',
      message: 'Custom validation formulas must start with "=".',
    })
  }

  if (input.kind !== 'custom' && operatorUsesTwoFormulas(input.operator) && !input.formula2.trim()) {
    issues.push({
      field: 'formula2',
      message: 'Between and not-between validation rules need a maximum value or formula.',
    })
  }

  return issues
}

export function buildValidationRuleAuthoringPreview(
  input: ValidationRuleDraftInput,
): ValidationRuleAuthoringPreview {
  const lines: string[] = []
  if (input.kind === 'list') {
    if (input.listSourceMode === 'formula') {
      lines.push(`Allowed source: ${input.listValuesText.trim() || 'No source'}`)
    } else {
      const values = typedListValues(input.listValuesText)
      lines.push(`Allowed values: ${values.length > 0 ? values.join(', ') : 'No values'}`)
    }
  } else if (input.kind === 'custom') {
    lines.push(`Formula: ${input.formula1.trim() || 'No formula'}`)
  } else {
    const kindLabel = optionLabel(VALIDATION_KIND_OPTIONS, input.kind)
    const operatorLabel = optionLabel(VALIDATION_OPERATOR_OPTIONS, input.operator)
    const first = input.formula1.trim() || 'No value'
    if (operatorUsesTwoFormulas(input.operator)) {
      lines.push(`${kindLabel} ${operatorLabel} ${first} and ${input.formula2.trim() || 'No maximum'}`)
    } else {
      lines.push(`${kindLabel} ${operatorLabel} ${first}`)
    }
  }

  lines.push(`Blank cells: ${input.allowBlank ? 'allowed' : 'blocked'}`)
  const prompt = [input.inputTitle.trim(), input.inputMessage.trim()].filter(Boolean).join(' - ')
  if (prompt) {
    lines.push(`Input prompt: ${prompt}`)
  }
  const errorStyle = optionLabel(VALIDATION_ERROR_STYLE_OPTIONS, input.errorStyle)
  const error = [input.errorTitle.trim(), input.errorMessage.trim()].filter(Boolean).join(' - ')
  lines.push(error ? `Error alert: ${errorStyle} - ${error}` : `Error alert: ${errorStyle}`)
  return {
    title: 'Rule preview',
    lines,
  }
}

export function buildValidationRuleAuthoringPresentation(args: {
  currentRuleId?: string | null
  kind: SpreadsheetValidationRuleKind
  operator: SpreadsheetValidationRuleOperator
  listSourceMode: ValidationListSourceMode
}): ValidationRuleAuthoringPresentation {
  const currentRuleId = String(args.currentRuleId ?? '').trim()
  const showsListSource = args.kind === 'list'
  const showsCustomFormula = args.kind === 'custom'
  const showsComparison = !showsListSource && !showsCustomFormula
  const showsSecondFormula = showsComparison && operatorUsesTwoFormulas(args.operator)
  const firstFormulaLabel = showsCustomFormula
    ? 'Formula'
    : showsSecondFormula
      ? 'Minimum'
      : 'Value'
  const formulaInputMode = showsCustomFormula ? 'text' : formulaInputModeForKind(args.kind)
  return {
    currentRuleLabel: currentRuleId ? `Current rule: ${currentRuleId}` : null,
    kindField: {
      label: 'Allow',
      title: 'Validation type',
      options: VALIDATION_KIND_OPTIONS,
    },
    listSourceModeField: {
      label: 'Source type',
      title: 'List validation source type',
      options: VALIDATION_LIST_SOURCE_MODE_OPTIONS,
    },
    sourceField: {
      label: 'Source',
      placeholder: args.listSourceMode === 'formula' ? '=$A$1:$A$10' : 'East, West, North',
      title: args.listSourceMode === 'formula'
        ? 'Engine-evaluated range or formula source'
        : 'Comma-separated allowed values',
    },
    operatorField: {
      label: 'Data',
      title: 'Validation comparison',
      options: VALIDATION_OPERATOR_OPTIONS,
    },
    firstFormulaField: {
      label: firstFormulaLabel,
      title: showsCustomFormula ? 'Custom validation formula' : 'Validation value',
      placeholder: showsCustomFormula ? '=A1<>""' : undefined,
      inputMode: formulaInputMode,
    },
    secondFormulaField: {
      label: 'Maximum',
      title: 'Validation maximum',
      inputMode: formulaInputMode,
    },
    allowBlankField: {
      label: 'Ignore blank',
      title: 'Ignore blank',
    },
    inputTitleField: {
      label: 'Input title',
      placeholder: 'Optional prompt title',
      title: 'Validation input prompt title',
    },
    inputMessageField: {
      label: 'Input message',
      placeholder: 'Prompt shown when this cell is selected',
      title: 'Validation input message',
    },
    errorStyleField: {
      label: 'Error style',
      title: 'Validation error alert style',
      options: VALIDATION_ERROR_STYLE_OPTIONS,
    },
    errorTitleField: {
      label: 'Error title',
      placeholder: 'Optional alert title',
      title: 'Validation error alert title',
    },
    errorMessageField: {
      label: 'Error message',
      placeholder: 'Value does not match this rule',
      title: 'Validation error message',
    },
    showsListSource,
    showsCustomFormula,
    showsComparison,
    showsSecondFormula,
  }
}

export function buildValidationRuleAuthoringActionState(args: {
  applyLabel: string
  issues: ValidationRuleAuthoringIssue[]
}): ValidationRuleAuthoringActionState {
  const issueTitle = args.issues.map((issue) => issue.message).join(' ') || null
  return {
    canApply: args.issues.length === 0,
    applyTitle: issueTitle ?? args.applyLabel,
    issueTitle,
  }
}

export function buildValidationRuleAuthoringModel(args: {
  currentRuleId?: string | null
  state: ValidationRuleAuthoringState
  applyLabel: string
}): ValidationRuleAuthoringModel {
  const presentation = buildValidationRuleAuthoringPresentation({
    currentRuleId: args.state.id,
    kind: args.state.kind,
    operator: args.state.operator,
    listSourceMode: args.state.listSourceMode,
  })
  const draftInput = buildValidationRuleDraftInput({
    currentRuleId: args.currentRuleId,
    state: args.state,
  })
  const issues = validateValidationRuleDraft(draftInput)
  return {
    presentation,
    draftInput,
    issues,
    preview: buildValidationRuleAuthoringPreview(draftInput),
    actionState: buildValidationRuleAuthoringActionState({
      applyLabel: args.applyLabel,
      issues,
    }),
  }
}

export function buildValidationRuleAuthoringApplyPlan(input: ValidationRuleDraftInput): ValidationRuleAuthoringApplyPlan {
  const issues = validateValidationRuleDraft(input)
  if (issues.length > 0) {
    return {
      kind: 'blocked',
      issues,
    }
  }
  return {
    kind: 'applyRule',
    rule: buildValidationRuleDraft(input),
  }
}
